
import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useBlogPost } from '@/hooks/useBlogPosts';
import { Calendar, User, ArrowLeft, Share } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from "sonner";

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: post, isLoading, error } = useBlogPost(slug || '');
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col pt-16">
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-32 mb-6"></div>
            <div className="bg-white rounded-xl overflow-hidden shadow-md p-6 md:p-8">
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-6"></div>
              <div className="h-4 bg-gray-200 rounded w-48 mb-8"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Error al cargar el artículo</h1>
          <p className="mb-6">Hubo un problema al cargar el artículo. Por favor, intenta de nuevo.</p>
          <Button onClick={() => navigate('/blog')}>
            Volver al Blog
          </Button>
        </div>
      </div>
    );
  }
  
  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Artículo no encontrado</h1>
          <p className="mb-6">Lo sentimos, el artículo que buscas no existe o ha sido movido.</p>
          <Button onClick={() => navigate('/blog')}>
            Volver al Blog
          </Button>
        </div>
      </div>
    );
  }
  
  const paragraphs = post.content.split('\n\n').filter(p => p.trim() !== '');
  
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: window.location.href,
      })
      .then(() => console.log('Successful share'))
      .catch((error) => console.log('Error sharing', error));
    } else {
      // Fallback to copying the URL
      navigator.clipboard.writeText(window.location.href);
      toast.success("Enlace copiado al portapapeles");
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col pt-16">
      <article className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="mb-6">
          <Link to="/blog" className="flex items-center text-primary hover:text-primary/80 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al Blog
          </Link>
        </div>
        
        <div className="bg-white rounded-xl overflow-hidden shadow-md">
          <div className="p-6 md:p-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6">{post.title}</h1>
            
            <div className="flex flex-wrap justify-between items-center gap-4 mb-8 pb-6 border-b border-gray-200">
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(post.date).toLocaleDateString('es-ES', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
                </div>
                <div className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  <span>{post.author}</span>
                </div>
              </div>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center gap-2"
                onClick={handleShare}
              >
                <Share className="h-4 w-4" />
                Compartir
              </Button>
            </div>
            
            <div className="prose max-w-none">
              {paragraphs.map((paragraph, index) => (
                <p key={index} className="mb-4 text-gray-700 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
            
            <div className="mt-12 pt-6 border-t border-gray-200">
              <h2 className="text-xl font-bold mb-4">¿Necesitas ayuda con tu situación migratoria?</h2>
              <p className="mb-6">
                En MigranteApp.com, te ofrecemos orientación gratuita y confidencial para tus dudas migratorias.
              </p>
              <Link to="/login">
                <Button className="bg-primary hover:bg-primary/90">Obtener ayuda gratuita</Button>
              </Link>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogDetail;
